import React, { useEffect, useMemo, useRef, useState } from "react";
import ElectionMap from "./Map";
import AdminPage from "./AdminPage";
import AnalyticsOverviewPage from "./AnalyticsOverviewPage";
import CandidatesPage from "./CandidatesPage";
import EducationPage from "./EducationPage";
import LandmarksPage from "./LandmarksPage";
import ReportsPage from "./ReportsPage";
import TrendsPage from "./TrendsPage";
import TsunamiPage from "./TsunamiPage";
import LandingPage from "./LandingPage";
import {
  ANALYTICS_CONSENT_ACCEPTED,
  ANALYTICS_CONSENT_REJECTED,
  getAnalyticsConsent,
  initClarity,
  initGa,
  setAnalyticsConsent,
  trackPageView,
} from "./ga";
import {
  APP_PAGE_BACKGROUND,
  BRAND_COLORS,
  ElectionMapLogo,
} from "./branding";
import { AuthProvider, resolveAppPath, useAuth } from "./auth";
import { APP_MODULES, MODULE_IDS, getModuleById, getModuleByPath } from "./modules";
import { RouterLink, SimpleRouterProvider, useRouter } from "./router";

const APP_TOP_BAR_HEIGHT = 36;

function isNavPathActive(currentPath, targetPath) {
  const path = typeof currentPath === "string" ? currentPath : "";
  if (targetPath === "/app") return path === "/app" || path === "/app/welcome";
  return path === targetPath || path.startsWith(`${targetPath}/`);
}

const shellStyles = {
  page: {
    minHeight: "100vh",
    background: APP_PAGE_BACKGROUND,
  },
  topBar: {
    display: "grid",
    gap: 0,
    padding: "2px var(--app-shell-gutter)",
    minHeight: APP_TOP_BAR_HEIGHT,
    boxSizing: "border-box",
    border: "1px solid rgba(203, 213, 225, 0.78)",
    borderRadius: 0,
    background: "#ffffff",
    boxShadow: "0 1px 0 rgba(15, 23, 42, 0.08)",
    backdropFilter: "none",
  },
  topBarMain: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  topBarNav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    flexWrap: "wrap",
    flex: "1 1 auto",
    minWidth: 0,
    marginLeft: "auto",
  },
  topLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 32,
    padding: "0 1px",
    color: "#1e293b",
    textDecoration: "none",
    fontSize: 14,
    fontFamily: "inherit",
    fontWeight: 600,
    lineHeight: 1,
    outline: "none",
    boxShadow: "none",
    borderBottom: "none",
    transition: "color 140ms ease, box-shadow 140ms ease, outline-color 140ms ease",
  },
  topLinkActive: {
    color: BRAND_COLORS.primaryDarkBlue,
    fontWeight: 700,
  },
  topLinkLabel: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 1,
  },
  brandLink: {
    display: "inline-flex",
    alignItems: "center",
    minWidth: 0,
    textDecoration: "none",
  },
  brandGroup: {
    display: "inline-flex",
    alignItems: "center",
    gap: 12,
    flex: "0 0 auto",
  },
  brandDivider: {
    width: 1,
    alignSelf: "stretch",
    background: "linear-gradient(180deg, rgba(203, 213, 225, 0.12) 0%, rgba(203, 213, 225, 0.88) 50%, rgba(203, 213, 225, 0.12) 100%)",
  },
  moduleTitle: {
    color: BRAND_COLORS.primaryDarkBlue,
    fontSize: 17,
    fontWeight: 900,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
  topBarPrimaryNav: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    minWidth: 0,
    justifyContent: "flex-end",
  },
  topBarControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
    minWidth: 0,
  },
  dropdownWrap: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
  },
  dropdownMenu: {
    position: "absolute",
    top: "calc(100% + 12px)",
    left: "50%",
    minWidth: 168,
    padding: 8,
    border: "1px solid #d7e3f3",
    borderRadius: 12,
    background: "white",
    boxShadow: "0 18px 36px rgba(15, 23, 42, 0.14)",
    display: "grid",
    gap: 2,
    zIndex: 5100,
  },
  dropdownLink: {
    display: "flex",
    alignItems: "center",
    minHeight: 34,
    padding: "8px 10px",
    borderRadius: 8,
    color: "#1e293b",
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 700,
  },
  areaSwitcherWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  iconBtn: {
    width: 30,
    height: 30,
    minWidth: 30,
    border: "1px solid #d7e3f3",
    borderRadius: 8,
    background: "rgba(255, 255, 255, 0.94)",
    color: "#1e293b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    textDecoration: "none",
  },
  areaSelect: {
    minWidth: 148,
    border: "1px solid #d7e3f3",
    borderRadius: 8,
    padding: "5px 9px",
    background: "rgba(255, 255, 255, 0.94)",
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 600,
  },
  areaStatus: {
    fontSize: 12,
    color: "#334155",
  },
  inlineError: {
    fontSize: 12,
    color: "#991b1b",
  },
  topBtn: {
    minHeight: 30,
    border: "1px solid #d7e3f3",
    borderRadius: 8,
    padding: "5px 9px",
    background: "rgba(255, 255, 255, 0.94)",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    color: "#1e293b",
  },
  container: {
    width: "100%",
    maxWidth: 760,
    margin: 0,
    padding: "22px 0 40px",
    display: "grid",
    gap: 12,
  },
  publicPage: {
    minHeight: "100vh",
    background: APP_PAGE_BACKGROUND,
    padding: "32px 0 48px",
  },
  publicPageLogo: {
    display: "inline-flex",
    alignItems: "center",
    marginBottom: 16,
    textDecoration: "none",
  },
  card: {
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    background: "rgba(255, 255, 255, 0.98)",
    boxShadow: "0 20px 44px rgba(15, 23, 42, 0.08)",
    backdropFilter: "blur(10px)",
    padding: 18,
    display: "grid",
    gap: 12,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "9px 10px",
    fontSize: 14,
  },
  submitBtn: {
    border: `1px solid ${BRAND_COLORS.primaryDarkBlue}`,
    borderRadius: 8,
    padding: "9px 10px",
    background: BRAND_COLORS.primaryDarkBlue,
    color: "white",
    cursor: "pointer",
    fontWeight: 600,
  },
  subtleBtn: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "8px 10px",
    background: "white",
    cursor: "pointer",
    fontSize: 13,
  },
  error: {
    border: "1px solid #fecaca",
    borderRadius: 8,
    background: "#fef2f2",
    color: "#991b1b",
    padding: "8px 10px",
    fontSize: 13,
  },
  message: {
    border: "1px solid #bfdbfe",
    borderRadius: 8,
    background: "#eff6ff",
    color: "#1e3a8a",
    padding: "8px 10px",
    fontSize: 13,
  },
  consentBanner: {
    position: "fixed",
    left: 16,
    right: 16,
    bottom: 16,
    zIndex: 2000,
    maxWidth: 760,
    margin: "0 auto",
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    background: "rgba(255, 255, 255, 0.98)",
    boxShadow: "0 18px 44px rgba(15, 23, 42, 0.16)",
    padding: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  consentText: {
    flex: "1 1 360px",
    margin: 0,
    color: "#1e293b",
    fontSize: 13,
    lineHeight: 1.45,
  },
  consentActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },
  consentAcceptBtn: {
    border: `1px solid ${BRAND_COLORS.primaryDarkBlue}`,
    borderRadius: 8,
    padding: "9px 11px",
    background: BRAND_COLORS.primaryDarkBlue,
    color: "white",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
  },
  consentRejectBtn: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "9px 11px",
    background: "white",
    color: "#334155",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
  },
};

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

async function activateInvite({ token, password, name }) {
  const response = await fetch("/api/activate", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      token,
      password,
      name,
    }),
  });
  const payload = await parseJsonSafe(response);

  if (!response.ok) {
    const message =
      (payload && typeof payload.error === "string" && payload.error) || "Activation failed";
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload && typeof payload === "object" ? payload : {};
}

function getActivateTokenFromPath(path) {
  const prefix = "/activate/";
  if (!path.startsWith(prefix)) return "";
  const rawToken = path.slice(prefix.length);
  if (!rawToken || rawToken.includes("/")) return "";
  try {
    return decodeURIComponent(rawToken);
  } catch (_) {
    return rawToken;
  }
}

function Navigate({ to, replace = true }) {
  const { navigate } = useRouter();
  useEffect(() => {
    navigate(to, { replace });
  }, [to, replace, navigate]);
  return null;
}

function SessionLoading() {
  return (
    <div style={shellStyles.container}>
      <div style={shellStyles.card}>Checking session...</div>
    </div>
  );
}

function PublicPageShell({ children, showLogo = false }) {
  return (
    <div style={shellStyles.publicPage}>
      <div style={shellStyles.container}>
        {showLogo ? (
          <RouterLink to="/" style={shellStyles.publicPageLogo} aria-label="ElectionMap home">
            <ElectionMapLogo height={34} />
          </RouterLink>
        ) : null}
        {children}
      </div>
    </div>
  );
}

function LoginPage() {
  const auth = useAuth();
  const { status, isAuthenticated, login } = auth;
  const { navigate } = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (status === "loading") return <SessionLoading />;
  if (isAuthenticated) return <Navigate to={resolveAppPath(auth)} />;

  async function onSubmit(event) {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      const session = await login({ email, password });
      navigate(resolveAppPath(session), { replace: true });
    } catch (err) {
      setError(err && err.message ? String(err.message) : "Login failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <PublicPageShell showLogo>
      <form style={shellStyles.card} onSubmit={onSubmit}>
        <h2 style={{ margin: 0 }}>Login</h2>
        <label>
          <div style={{ marginBottom: 6, fontSize: 13 }}>Email</div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={shellStyles.input}
            required
          />
        </label>
        <label>
          <div style={{ marginBottom: 6, fontSize: 13 }}>Password</div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={shellStyles.input}
            required
          />
        </label>
        {error ? <div style={shellStyles.error}>{error}</div> : null}
        <button type="submit" style={shellStyles.submitBtn} disabled={isSubmitting}>
          {isSubmitting ? "Signing in..." : "Sign in"}
        </button>
        <div style={{ fontSize: 13 }}>
          Need access?{" "}
          <RouterLink to="/request-access" style={shellStyles.topLink}>
            Request access
          </RouterLink>
        </div>
      </form>
    </PublicPageShell>
  );
}

function ActivationPage({ token }) {
  const auth = useAuth();
  const { navigate } = useRouter();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function onSubmit(event) {
    event.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = await activateInvite({ token, password, name });
      await auth.refreshSession();
      const redirect =
        payload && typeof payload.redirect === "string" && payload.redirect.trim()
          ? payload.redirect.trim()
          : "/app/welcome";
      navigate(redirect, { replace: true });
    } catch (err) {
      if (err && err.status === 409) {
        setError("Account already exists. Contact support.");
      } else if (err && (err.status === 400 || err.status === 401)) {
        setError(err && err.message ? String(err.message) : "Activation failed");
      } else {
        setError(err && err.message ? String(err.message) : "Activation failed");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <PublicPageShell showLogo>
      <form style={shellStyles.card} onSubmit={onSubmit}>
        <h2 style={{ margin: 0 }}>Welcome to ElectionMap</h2>
        <p style={{ margin: 0, opacity: 0.85 }}>Set your password to activate your account.</p>
        <label>
          <div style={{ marginBottom: 6, fontSize: 13 }}>Name (optional)</div>
          <input value={name} onChange={(e) => setName(e.target.value)} style={shellStyles.input} />
        </label>
        <label>
          <div style={{ marginBottom: 6, fontSize: 13 }}>Password</div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={shellStyles.input}
            minLength={8}
            required
          />
        </label>
        <label>
          <div style={{ marginBottom: 6, fontSize: 13 }}>Confirm password</div>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={shellStyles.input}
            minLength={8}
            required
          />
        </label>
        {error ? <div style={shellStyles.error}>{error}</div> : null}
        <button type="submit" style={shellStyles.submitBtn} disabled={isSubmitting}>
          {isSubmitting ? "Activating..." : "Activate account"}
        </button>
      </form>
    </PublicPageShell>
  );
}

function ThankYouPage() {
  return (
    <PublicPageShell showLogo>
      <div style={shellStyles.card}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Žádost jsme přijali</h1>
        <p style={{ margin: 0, opacity: 0.85 }}>
          Děkujeme. Teď projdeme uvedené území, požadované vrstvy a otázku, kterou řešíte.
          <br />
          Účet ani přístup nevznikají automaticky. Další postup navrhneme až po kontrole rozsahu
          a případné pozvánce.
        </p>
        <RouterLink to="/" style={shellStyles.topLink}>
          Zpět na úvod
        </RouterLink>
      </div>
    </PublicPageShell>
  );
}

function WaitingPage() {
  const { logout } = useAuth();
  const { navigate } = useRouter();
  const [error, setError] = useState("");

  async function onLogout() {
    setError("");
    try {
      await logout();
      navigate("/app/login", { replace: true });
    } catch (err) {
      setError(err && err.message ? String(err.message) : "Odhlášení selhalo");
    }
  }

  return (
    <div style={shellStyles.container}>
      <div style={shellStyles.card}>
        <h2 style={{ margin: 0 }}>Waiting For Access</h2>
        <p style={{ margin: 0 }}>Account created. Waiting for access.</p>
        {error ? <div style={shellStyles.error}>{error}</div> : null}
        <button type="button" style={shellStyles.subtleBtn} onClick={onLogout}>
          Odhlásit
        </button>
      </div>
    </div>
  );
}

function SettingsPage() {
  const { user, currentAreaName, availableAreas, logout } = useAuth();
  const { navigate } = useRouter();
  const [error, setError] = useState("");

  async function onLogout() {
    setError("");
    try {
      await logout();
      navigate("/app/login", { replace: true });
    } catch (err) {
      setError(err && err.message ? String(err.message) : "Odhlášení selhalo");
    }
  }

  return (
    <div style={shellStyles.container}>
      <div style={shellStyles.card}>
        <div>
          <strong>Jméno:</strong> {user && user.name ? user.name : "N/A"}
        </div>
        <div>
          <strong>Email:</strong> {user && user.email ? user.email : "N/A"}
        </div>
        <div>
          <strong>Aktuální oblast:</strong> {currentAreaName || "N/A"}
        </div>
        <div>
          <strong>Dostupné oblasti:</strong> {availableAreas.length}
        </div>
        {error ? <div style={shellStyles.error}>{error}</div> : null}
        <button type="button" style={shellStyles.subtleBtn} onClick={onLogout}>
          Odhlásit
        </button>
      </div>
    </div>
  );
}

function SettingsIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6l-.04.08a2 2 0 1 1-3.92 0L10 20a1.7 1.7 0 0 0-1-.6 1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1l-.08-.04a2 2 0 1 1 0-3.92L4 10a1.7 1.7 0 0 0 .6-1 1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6l.04-.08a2 2 0 1 1 3.92 0L14 4a1.7 1.7 0 0 0 1 .6 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.15.35.36.7.6 1l.08.04a2 2 0 1 1 0 3.92L20 14a1.7 1.7 0 0 0-.6 1Z" />
    </svg>
  );
}

function PlaceholderModulePage({ label }) {
  return (
    <main style={shellStyles.container}>
      <section style={shellStyles.card}>
        <p style={{ margin: 0, color: "#334155", lineHeight: 1.5 }}>
          Modul {label} je připravený v navigaci a oprávněních. Obsah bude doplněn podle dalšího
          produktového zadání.
        </p>
      </section>
    </main>
  );
}

function AreaSwitcher() {
  const { availableAreas, currentAreaId } = useAuth();
  const [error, setError] = useState("");
  const [isSwitching, setIsSwitching] = useState(false);

  if (!Array.isArray(availableAreas) || availableAreas.length === 0) return null;

  async function onChange(event) {
    const nextAreaId = Number(event.target.value);
    if (!Number.isInteger(nextAreaId) || nextAreaId <= 0 || nextAreaId === currentAreaId) return;

    setError("");
    setIsSwitching(true);

    try {
      const response = await fetch("/api/auth/switch-area", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ area_id: nextAreaId }),
      });
      const payload = await parseJsonSafe(response);

      if (!response.ok) {
        const message =
          (payload && typeof payload.error === "string" && payload.error) ||
          `Area switch failed (${response.status})`;
        throw new Error(message);
      }

      window.location.assign("/app");
    } catch (err) {
      setError(err && err.message ? String(err.message) : "Area switch failed");
      setIsSwitching(false);
    }
  }

  return (
    <div style={shellStyles.areaSwitcherWrap}>
      <select
        aria-label="Aktivní oblast"
        style={shellStyles.areaSelect}
        value={currentAreaId || availableAreas[0].id}
        onChange={onChange}
        disabled={isSwitching}
      >
        {availableAreas.map((area) => (
          <option key={area.id} value={area.id}>
            {area.name}
          </option>
        ))}
      </select>
      {isSwitching ? <span style={shellStyles.areaStatus}>Přepínám...</span> : null}
      {error ? <div style={shellStyles.inlineError}>{error}</div> : null}
    </div>
  );
}

function AppShell({ children, moduleId = "" }) {
  const auth = useAuth();
  const { canAccessAnyAdmin, adminForbidden, logout } = auth;
  const { navigate, path } = useRouter();
  const [error, setError] = useState("");
  const [topBarHeight, setTopBarHeight] = useState(APP_TOP_BAR_HEIGHT);
  const [isOtherOpen, setIsOtherOpen] = useState(false);
  const topBarRef = useRef(null);
  const otherDropdownRef = useRef(null);
  const activeModule = getModuleById(moduleId) || getModuleByPath(path) || getModuleById(MODULE_IDS.MAP);
  const moduleTitle = activeModule ? activeModule.label : "";
  const moduleDescription = activeModule ? activeModule.description : "";
  const shellTopOffset = topBarHeight;

  useEffect(() => {
    const measureTopBar = () => {
      const nextHeight = topBarRef.current && topBarRef.current.offsetHeight ? topBarRef.current.offsetHeight : APP_TOP_BAR_HEIGHT;
      setTopBarHeight(nextHeight);
    };

    measureTopBar();
    let observer = null;
    if (typeof ResizeObserver !== "undefined" && topBarRef.current) {
      observer = new ResizeObserver(measureTopBar);
      observer.observe(topBarRef.current);
    }
    window.addEventListener("resize", measureTopBar);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measureTopBar);
    };
  }, [moduleTitle]);

  useEffect(() => {
    setIsOtherOpen(false);
  }, [path]);

  useEffect(() => {
    if (!isOtherOpen) return undefined;
    const onPointerDown = (event) => {
      if (otherDropdownRef.current && otherDropdownRef.current.contains(event.target)) return;
      setIsOtherOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOtherOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOtherOpen]);

  async function onLogout() {
    setError("");
    try {
      await logout();
      navigate("/app/login", { replace: true });
    } catch (err) {
      setError(err && err.message ? String(err.message) : "Odhlášení selhalo");
    }
  }

  function canAccessModule(module) {
    if (!module.accessFlag) return true;
    return Boolean(auth[module.accessFlag]);
  }

  const coreNavItems = APP_MODULES.filter((module) => module.navGroup === "core" && canAccessModule(module)).sort(
    (a, b) => a.navOrder - b.navOrder
  );
  const otherNavItems = APP_MODULES.filter((module) => module.navGroup === "other" && canAccessModule(module)).sort(
    (a, b) => a.navOrder - b.navOrder
  );
  const adminModule = getModuleById(MODULE_IDS.ADMIN);
  const settingsModule = getModuleById(MODULE_IDS.SETTINGS);
  const isOtherActive = otherNavItems.some((item) => isNavPathActive(path, item.path));

  function renderNavLink(item, extraStyle = null) {
    const isActive = isNavPathActive(path, item.path);
    return (
      <RouterLink
        key={item.id}
        to={item.path}
        className={`app-shell-nav-link${isActive ? " is-active" : ""}`}
        style={{
          ...shellStyles.topLink,
          ...(extraStyle || null),
          ...(isActive ? shellStyles.topLinkActive : null),
        }}
        aria-current={isActive ? "page" : undefined}
        title={item.description || undefined}
      >
        <span className="app-shell-nav-link-label" style={shellStyles.topLinkLabel} title={item.description || undefined}>
          {item.label}
        </span>
      </RouterLink>
    );
  }

  return (
    <div
      className="app-shell-page"
      style={{
        ...shellStyles.page,
        paddingTop: shellTopOffset,
        "--app-shell-topbar-height": `${topBarHeight}px`,
        "--app-shell-topbar-offset": `${shellTopOffset}px`,
        "--app-shell-available-height": `calc(100vh - ${shellTopOffset}px)`,
      }}
    >
      <style>{`
        .app-shell-nav-link,
        .app-shell-nav-link:link,
        .app-shell-nav-link:visited,
        .app-shell-nav-link:active {
          text-decoration: none;
          border-bottom: none;
          box-shadow: none;
        }
        .app-shell-nav-link:focus {
          outline: 2px solid rgba(22, 102, 215, 0.22);
          outline-offset: 3px;
          border-radius: 8px;
        }
        .app-shell-nav-link:focus:not(:focus-visible) {
          outline: none;
        }
        .app-shell-nav-link:focus-visible {
          outline: 2px solid rgba(22, 102, 215, 0.28);
          outline-offset: 3px;
          border-radius: 8px;
        }
        .app-shell-topbar-frame {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 5000;
        }
        .app-shell-content-frame {
          padding: 0 var(--app-shell-gutter);
        }
        .app-shell-nav-link-label::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: -2px;
          height: 3px;
          border-radius: 999px;
          background: ${BRAND_COLORS.primaryBrightBlue};
          opacity: 0;
          transform: scaleX(0);
          transform-origin: center;
          transition: opacity 140ms ease, transform 140ms ease;
          pointer-events: none;
        }
        .app-shell-nav-link:hover .app-shell-nav-link-label::after,
        .app-shell-nav-link.is-active .app-shell-nav-link-label::after {
          opacity: 1;
          transform: scaleX(1);
        }
        .app-shell-topbar {
          border-left: 0 !important;
          border-right: 0 !important;
          border-top: 0 !important;
          border-radius: 0 !important;
          background: #ffffff !important;
          box-shadow: 0 1px 0 rgba(15, 23, 42, 0.08) !important;
          backdrop-filter: none !important;
        }
        .app-shell-brand-link {
          transform: translateX(-8px);
          margin-right: -8px;
        }
        .app-shell-topbar-main {
          gap: 8px;
          flex-wrap: nowrap;
        }
        .app-shell-brand-group {
          gap: 8px;
        }
        .app-shell-module-title {
          font-size: 14px;
        }
        .app-shell-topbar-nav {
          gap: 8px;
          flex-wrap: nowrap;
        }
        .app-shell-primary-nav {
          gap: 10px;
          flex-wrap: nowrap;
        }
        .app-shell-nav-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 32px;
          padding-top: 0;
          padding-bottom: 0;
          appearance: none;
          vertical-align: middle;
        }
        .app-shell-settings-link {
          width: 30px;
          height: 30px;
          min-width: 30px;
        }
        .app-shell-topbar select {
          padding-top: 5px;
          padding-bottom: 5px;
        }
        .app-shell-topbar button:not(.app-shell-nav-link) {
          min-height: 30px;
        }
        .app-shell-dropdown-menu {
          opacity: 0;
          transform: translate(-50%, -4px);
          pointer-events: none;
          transition: opacity 120ms ease, transform 120ms ease;
        }
        .app-shell-dropdown.is-open .app-shell-dropdown-menu {
          opacity: 1;
          transform: translate(-50%, 0);
          pointer-events: auto;
          z-index: 5100;
        }
        .app-shell-dropdown-link:hover,
        .app-shell-dropdown-link.is-active {
          background: #eff6ff;
          color: ${BRAND_COLORS.primaryDarkBlue};
        }
        .app-shell-settings-link.is-active {
          color: ${BRAND_COLORS.primaryDarkBlue};
          border-color: rgba(22, 102, 215, 0.42);
          background: #eff6ff;
        }
        @media (max-width: 980px) {
          .app-shell-topbar-main,
          .app-shell-topbar-nav,
          .app-shell-primary-nav {
            flex-wrap: wrap;
          }
        }
      `}</style>
      <div className="app-shell-topbar-frame">
        <header ref={topBarRef} className="app-shell-topbar" style={shellStyles.topBar}>
          <div className="app-shell-topbar-main" style={shellStyles.topBarMain}>
            <div className="app-shell-brand-group" style={shellStyles.brandGroup}>
              <RouterLink
                to="/app"
                className="app-shell-brand-link"
                style={shellStyles.brandLink}
                aria-label="ElectionMap app home"
              >
                <ElectionMapLogo height={29} />
              </RouterLink>
              <div style={shellStyles.brandDivider} aria-hidden="true" />
              {moduleTitle ? (
                <div
                  className="app-shell-module-title"
                  style={shellStyles.moduleTitle}
                  title={moduleDescription || undefined}
                  aria-label={moduleDescription ? `${moduleTitle}: ${moduleDescription}` : moduleTitle}
                >
                  {moduleTitle}
                </div>
              ) : null}
            </div>
            <nav className="app-shell-topbar-nav" style={shellStyles.topBarNav}>
              <div className="app-shell-primary-nav" style={shellStyles.topBarPrimaryNav}>
                {coreNavItems.map((item) => renderNavLink(item))}
                {otherNavItems.length > 0 ? (
                  <div
                    className={`app-shell-dropdown${isOtherOpen ? " is-open" : ""}`}
                    style={shellStyles.dropdownWrap}
                    ref={otherDropdownRef}
                  >
                    <button
                      type="button"
                      className={`app-shell-nav-link${isOtherActive ? " is-active" : ""}`}
                      style={{
                        ...shellStyles.topLink,
                        ...(isOtherActive ? shellStyles.topLinkActive : null),
                        border: 0,
                        background: "transparent",
                        cursor: "pointer",
                      }}
                      aria-haspopup="menu"
                      aria-expanded={isOtherOpen}
                      title="Další moduly"
                      onClick={() => setIsOtherOpen((open) => !open)}
                    >
                      <span className="app-shell-nav-link-label" style={shellStyles.topLinkLabel} title="Další moduly">
                        Ostatní
                      </span>
                    </button>
                    <div className="app-shell-dropdown-menu" style={shellStyles.dropdownMenu} role="menu">
                      {otherNavItems.map((item) => {
                        const isActive = isNavPathActive(path, item.path);
                        return (
                          <RouterLink
                            key={item.id}
                            to={item.path}
                            className={`app-shell-dropdown-link${isActive ? " is-active" : ""}`}
                            style={shellStyles.dropdownLink}
                            role="menuitem"
                            aria-current={isActive ? "page" : undefined}
                            title={item.description || undefined}
                            onClick={() => setIsOtherOpen(false)}
                          >
                            <span title={item.description || undefined}>{item.label}</span>
                          </RouterLink>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                {canAccessAnyAdmin && !adminForbidden && adminModule ? renderNavLink(adminModule) : null}
              </div>
              <div style={shellStyles.topBarControls}>
                <AreaSwitcher />
                {settingsModule ? (
                  <RouterLink
                    to={settingsModule.path}
                    className={`app-shell-settings-link${isNavPathActive(path, settingsModule.path) ? " is-active" : ""}`}
                    style={shellStyles.iconBtn}
                    aria-label="Nastavení"
                    title="Nastavení"
                  >
                    <SettingsIcon />
                  </RouterLink>
                ) : null}
                <button type="button" style={shellStyles.topBtn} onClick={onLogout}>
                  Odhlásit
                </button>
              </div>
            </nav>
          </div>
        </header>
      </div>
      <div className="app-shell-content-frame">
        {error ? (
          <div style={{ maxWidth: 760, paddingTop: 12 }}>
            <div style={shellStyles.error}>{error}</div>
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

function ProtectedAppRoutes({ path }) {
  const auth = useAuth();
  const isAdminPath =
    path === "/app/admin" ||
    path === "/app/admin/requests" ||
    path === "/app/admin/invites" ||
    path === "/app/admin/users";
  const isAnalyticsPath = path === "/app/analytics" || path === "/app/analytics/overview";
  const isEducationPath = path === "/app/education";
  const isTrendsPath = path === "/app/trends";
  const isTsunamiPath = path === "/app/tsunami";
  const isLandmarksPath = path === "/app/landmarks";
  const isCandidatesPath = path === "/app/candidates";
  const isReportsPath = path === "/app/reports";
  const isQuartersPath = path === "/app/quarters";
  const isTestingPath = path === "/app/testing";

  if (auth.status === "loading") return <SessionLoading />;
  if (!auth.isAuthenticated) return <Navigate to="/app/login" replace />;

  if (isAdminPath) {
    if (!auth.canAccessAnyAdmin) return <Navigate to="/app" replace />;
    const adminTab =
      path === "/app/admin/invites" ? "invites" : path === "/app/admin/users" ? "users" : "requests";
    if (adminTab === "users" && !(auth.user && auth.user.is_superadmin)) {
      return <Navigate to="/app/admin/requests" replace />;
    }
    return (
      <AppShell moduleId={MODULE_IDS.ADMIN}>
        <AdminPage initialTab={adminTab} />
      </AppShell>
    );
  }

  if (auth.memberships.length === 0) {
    if (!auth.currentAreaId) {
      if (path !== "/app/waiting") return <Navigate to="/app/waiting" replace />;
      return (
        <AppShell>
          <WaitingPage />
        </AppShell>
      );
    }
  }

  if (path === "/app" || path === "/app/welcome") {
    return (
      <AppShell moduleId={MODULE_IDS.MAP}>
        <ElectionMap areaId={auth.currentAreaId} />
      </AppShell>
    );
  }

  if (isAnalyticsPath) {
    if (!auth.canAccessAnalytics) return <Navigate to="/app" replace />;
    if (path === "/app/analytics") return <Navigate to="/app/analytics/overview" replace />;
    return (
      <AppShell moduleId={MODULE_IDS.ANALYTICS}>
        <AnalyticsOverviewPage areaId={auth.currentAreaId} />
      </AppShell>
    );
  }

  if (isTrendsPath) {
    if (!auth.canAccessTrends) return <Navigate to="/app" replace />;
    return (
      <AppShell moduleId={MODULE_IDS.TRENDS}>
        <TrendsPage areaId={auth.currentAreaId} />
      </AppShell>
    );
  }

  if (isTsunamiPath) {
    if (!auth.canAccessTsunami) return <Navigate to="/app" replace />;
    return (
      <AppShell moduleId={MODULE_IDS.TSUNAMI}>
        <TsunamiPage areaId={auth.currentAreaId} />
      </AppShell>
    );
  }

  if (isLandmarksPath) {
    if (!auth.canAccessLandmarks) return <Navigate to="/app" replace />;
    return (
      <AppShell moduleId={MODULE_IDS.LANDMARKS}>
        <LandmarksPage areaId={auth.currentAreaId} />
      </AppShell>
    );
  }

  if (isCandidatesPath) {
    if (!auth.canAccessCandidates) return <Navigate to="/app" replace />;
    return (
      <AppShell moduleId={MODULE_IDS.CANDIDATES}>
        <CandidatesPage areaId={auth.currentAreaId} />
      </AppShell>
    );
  }

  if (isReportsPath) {
    if (!auth.canAccessReports) return <Navigate to="/app" replace />;
    return (
      <AppShell moduleId={MODULE_IDS.REPORTS}>
        <ReportsPage />
      </AppShell>
    );
  }

  if (isEducationPath) {
    if (!auth.canAccessEducation) return <Navigate to="/app" replace />;
    return (
      <AppShell moduleId={MODULE_IDS.EDUCATION}>
        <EducationPage areaId={auth.currentAreaId} />
      </AppShell>
    );
  }

  if (isQuartersPath) {
    if (!auth.canAccessQuarters) return <Navigate to="/app" replace />;
    return (
      <AppShell moduleId={MODULE_IDS.QUARTERS}>
        <PlaceholderModulePage label="Čtvrti" />
      </AppShell>
    );
  }

  if (isTestingPath) {
    if (!auth.canAccessTesting) return <Navigate to="/app" replace />;
    return (
      <AppShell moduleId={MODULE_IDS.TESTING}>
        <PlaceholderModulePage label="Testing" />
      </AppShell>
    );
  }

  if (path === "/app/settings") {
    return (
      <AppShell moduleId={MODULE_IDS.SETTINGS}>
        <SettingsPage />
      </AppShell>
    );
  }

  if (path === "/app/waiting") return <Navigate to="/app" replace />;
  return <Navigate to="/app" replace />;
}

function PublicAuthRoute({ children }) {
  const auth = useAuth();
  if (auth.status === "loading") return <SessionLoading />;
  if (auth.isAuthenticated) return <Navigate to={resolveAppPath(auth)} replace />;
  return children;
}

function isMarketingIndexablePath(path) {
  return path === "/";
}

function isBetaHost(hostname) {
  return hostname === "beta.electionmap.cz";
}

const PUBLIC_CANONICAL_URL = "https://electionmap.cz/";

function getRouteMetadata(path) {
  const hostname =
    typeof window !== "undefined" && window.location && window.location.hostname
      ? window.location.hostname
      : "electionmap.cz";
  const isIndexable = isMarketingIndexablePath(path) && !isBetaHost(hostname);

  return {
    title: isIndexable ? "ElectionMap" : "ElectionMap",
    description:
      "ElectionMap je mapová aplikace pro srozumitelnou práci s volebními výsledky a územní analýzou.",
    robots: isIndexable ? "index,follow" : "noindex,nofollow",
    canonical: PUBLIC_CANONICAL_URL,
  };
}

function upsertMetaTag(selector, createAttrs, content) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(createAttrs).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonicalLink(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function RouteMetadata({ path }) {
  useEffect(() => {
    const metadata = getRouteMetadata(path);
    document.title = metadata.title;
    upsertMetaTag('meta[name="description"]', { name: "description" }, metadata.description);
    upsertMetaTag('meta[name="robots"]', { name: "robots" }, metadata.robots);
    upsertCanonicalLink(metadata.canonical);
  }, [path]);

  return null;
}

function AnalyticsRouteTracker({ path }) {
  useEffect(() => {
    initClarity();
    if (initGa()) trackPageView(path);
  }, [path]);

  return null;
}

function CookieConsentBanner({ path }) {
  const [consent, setConsent] = useState(() => getAnalyticsConsent());

  if (consent) return null;

  const acceptAnalytics = () => {
    setAnalyticsConsent(ANALYTICS_CONSENT_ACCEPTED);
    setConsent(ANALYTICS_CONSENT_ACCEPTED);
    initClarity();
    if (initGa()) trackPageView(path);
  };
  const rejectAnalytics = () => {
    setAnalyticsConsent(ANALYTICS_CONSENT_REJECTED);
    setConsent(ANALYTICS_CONSENT_REJECTED);
  };

  return (
    <section style={shellStyles.consentBanner} aria-label="Nastavení analytických cookies">
      <p style={shellStyles.consentText}>
        Používáme nezbytné cookies pro fungování webu. S vaším souhlasem používáme také analytické
        cookies, které nám pomáhají zlepšovat ElectionMap. Souhlas můžete odmítnout.
      </p>
      <div style={shellStyles.consentActions}>
        <button type="button" style={shellStyles.consentAcceptBtn} onClick={acceptAnalytics}>
          Povolit analytiku
        </button>
        <button type="button" style={shellStyles.consentRejectBtn} onClick={rejectAnalytics}>
          Odmítnout
        </button>
      </div>
    </section>
  );
}

function RootRouter() {
  const { path } = useRouter();

  const currentPath = useMemo(() => {
    if (path === "/") return "/";
    return path.endsWith("/") && path.length > 1 ? path.slice(0, -1) : path;
  }, [path]);
  const activateToken = useMemo(() => getActivateTokenFromPath(currentPath), [currentPath]);

  let route;

  if (currentPath === "/") route = <LandingPage />;
  else if (activateToken) route = <ActivationPage token={activateToken} />;
  else if (currentPath === "/request-access") route = <LandingPage autoScrollToForm />;
  else if (currentPath === "/thank-you") route = <ThankYouPage />;
  else if (currentPath === "/app/login") {
    route = (
      <PublicAuthRoute>
        <LoginPage />
      </PublicAuthRoute>
    );
  } else if (currentPath === "/app/register") {
    route = <Navigate to="/request-access" replace />;
  } else if (currentPath.startsWith("/app")) route = <ProtectedAppRoutes path={currentPath} />;
  else route = <Navigate to="/" replace />;

  return (
    <>
      <RouteMetadata path={currentPath} />
      <AnalyticsRouteTracker path={currentPath} />
      {route}
      <CookieConsentBanner path={currentPath} />
    </>
  );
}

export default function App() {
  return (
    <SimpleRouterProvider>
      <AuthProvider>
        <RootRouter />
      </AuthProvider>
    </SimpleRouterProvider>
  );
}
